/**
 * Timezone rules for Aurora TMS — read docs/TIMEZONES.md for the full story.
 *
 *   1. INPUT  — every order time a person types is Central time (PLANNING_TZ),
 *               no matter where the person or the stop is. The order
 *               create/edit forms show Central. Use `formLocalToUtc` /
 *               `utcToFormLocal`.
 *   2. VIEW   — every saved time is shown in the viewer's own timezone, always
 *               with an abbreviation. Components get the zone from
 *               `useViewerTimezone()` and format with `@/lib/order-time`.
 *   3. OUTPUT — text that leaves the screen (emails, partner change summaries,
 *               notifications, CSV exports) is written in Central.
 *
 * The database only ever stores UTC instants. This module is the only place
 * allowed to import date-fns-tz or name an IANA zone (enforced by
 * src/lib/timezone.guard.test.ts).
 */
import { formatInTimeZone, getTimezoneOffset } from "date-fns-tz";

/** The single timezone all order planning and dispatch input is entered in.
 *  Mirrored for edge functions in `supabase/functions/_shared/planning-tz.ts`
 *  (a parity test keeps the two equal). */
export const PLANNING_TZ = "America/Chicago";

/** Zone recorded on recurring templates created before ASE-17444; they keep
 *  generating in Pacific until converted (see recurringTemplateForm.ts). */
export const LEGACY_TEMPLATE_TZ = "America/Los_Angeles";

const FORM_LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

/** `"YYYY-MM-DDTHH:mm[:ss]"` with sane field ranges → `"YYYY-MM-DDTHH:mm"`,
 *  else `null` (empty, half-typed, or carrying `Z`/an offset). */
function parseFormLocal(value: string): string | null {
  const m = FORM_LOCAL_RE.exec(value);
  if (!m) return null;
  const [mo, d, h, mi] = [m[2], m[3], m[4], m[5]].map(Number);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
}

/** Parse a stored timestamp (ISO or Postgres `"… +00"` style). Requires an
 *  explicit `Z`/offset so a naive local string is never mistaken for UTC. */
function parseUtc(value: string | null | undefined): Date | null {
  if (!value) return null;
  const v = value.trim().replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00");
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(v)) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Convert a wall-clock date + time in `tz` to a UTC ISO string, applying the
 *  app's DST rule (used by order input and by recurring templates):
 *   - spring-forward gap (02:30 on the March change day doesn't exist):
 *     move FORWARD, so 02:30 → 03:30 CDT;
 *   - fall-back overlap (01:30 on the November change day happens twice):
 *     use the FIRST occurrence (CDT).
 *  Returns `null` if the inputs don't form a valid time. */
export function wallClockToUtc(date: string, time: string, tz: string): string | null {
  const wall = `${date}T${time.slice(0, 5)}`;
  const naiveMs = Date.parse(`${wall}:00Z`); // the wall-clock digits read as if UTC
  if (Number.isNaN(naiveMs)) return null;
  // Resolve explicitly instead of trusting date-fns-tz, whose choice for
  // ambiguous times depends on the *machine's* timezone. The zone's offset 12h
  // either side of the wall time brackets any DST change on that day.
  const HALF_DAY = 12 * 60 * 60 * 1000;
  const before = getTimezoneOffset(tz, new Date(naiveMs - HALF_DAY));
  const after = getTimezoneOffset(tz, new Date(naiveMs + HALF_DAY));
  if (Number.isNaN(before) || Number.isNaN(after)) return null;
  const matches = [before, after]
    .map((off) => naiveMs - off)
    .filter((ms) => formatInTimeZone(new Date(ms), tz, "yyyy-MM-dd'T'HH:mm") === wall)
    .sort((a, b) => a - b);
  // Overlap: two matches → earliest. Gap: none → use the pre-change offset,
  // which lands the same distance past the gap.
  return new Date(matches[0] ?? naiveMs - before).toISOString();
}

/** Order-form value (`"YYYY-MM-DDTHH:mm"`, Central) → stored UTC ISO, or
 *  `null` when empty or still being typed. Already-UTC values pass through
 *  unchanged; a bare `"YYYY-MM-DD"` means midnight Central. This is the one
 *  conversion every order input path uses. */
export function formLocalToUtc(local: string | null | undefined): string | null {
  if (!local) return null;
  // A value that already carries Z / an offset is an unambiguous instant
  // (e.g. legs hydrated straight from the DB) — never reinterpret it.
  const alreadyUtc = parseUtc(local);
  if (alreadyUtc) return alreadyUtc.toISOString();
  const wall = parseFormLocal(/^\d{4}-\d{2}-\d{2}$/.test(local) ? `${local}T00:00` : local);
  if (!wall) return null;
  const [date, time] = wall.split("T");
  return wallClockToUtc(date, time, PLANNING_TZ);
}

/** Stored UTC → the Central `"YYYY-MM-DDTHH:mm"` a form input shows, or `""`.
 *  Inverse of `formLocalToUtc`; hydrating and saving must use this pair so an
 *  unchanged save never shifts a time. */
export function utcToFormLocal(iso: string | null | undefined): string {
  const d = parseUtc(iso);
  return d ? formatInTimeZone(d, PLANNING_TZ, "yyyy-MM-dd'T'HH:mm") : "";
}

/** "CST" or "CDT" for the form value being entered (not for today). Falls back
 *  to the current abbreviation when the field is empty or still being typed. */
export function planningTzAbbr(local: string | null | undefined, now: Date = new Date()): string {
  const utc = local ? formLocalToUtc(local) : null;
  return formatInTimeZone(utc ? new Date(utc) : now, PLANNING_TZ, "zzz");
}

/** Current Central date and time, for "can't schedule in the past" floors. */
export function getNowInPlanningTz(now: Date = new Date()): { date: string; time: string } {
  return {
    date: formatInTimeZone(now, PLANNING_TZ, "yyyy-MM-dd"),
    time: formatInTimeZone(now, PLANNING_TZ, "HH:mm"),
  };
}

/** Format a stored instant in `tz` (with `zzz` in `fmt` for the abbreviation).
 *  The single display primitive; `@/lib/order-time` builds on it. Returns ""
 *  for empty input. */
export function formatInZone(iso: string | null | undefined, tz: string, fmt: string): string {
  const d = parseUtc(iso);
  return d ? formatInTimeZone(d, tz, fmt) : "";
}

/** The viewer's IANA timezone. Only `useViewerTimezone` should call this —
 *  everything else receives the zone as an argument so it stays testable. */
export function resolveViewerTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      formatInTimeZone(new Date(0), tz, "zzz"); // throws on an unknown zone
      return tz;
    }
  } catch {
    /* fall through */
  }
  return PLANNING_TZ;
}

/** Returns the operational day containing `now`. Defaults to the planning
 *  timezone (Central) so every dispatcher, wherever they are, sees the same
 *  "today" buckets on the dashboard. */
export function getOperationalDayBounds(
  now: Date = new Date(),
  tz: string = PLANNING_TZ,
): { date: string; startIso: string; endIso: string } {
  const date = formatInTimeZone(now, tz, "yyyy-MM-dd");
  const [y, m, d] = date.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  const nextStart = Date.parse(wallClockToUtc(next, "00:00", tz)!);
  return {
    date,
    startIso: wallClockToUtc(date, "00:00", tz)!,
    endIso: new Date(nextStart - 1).toISOString(),
  };
}

/** Format a cancellation timestamp in the given (viewer) timezone as
 *  `"on MMM d at HH:mm TZ"`, inserting the year only when it differs from the
 *  current year in that timezone.
 *
 *  Examples (today is in 2026):
 *    formatCancellationTimestamp("2026-06-09T21:32:00Z") → "on Jun 9 at 14:32 PDT"
 *    formatCancellationTimestamp("2025-12-31T23:59:00Z") → "on Dec 31, 2025 at 15:59 PST"
 */
export function formatCancellationTimestamp(
  utcIso: string | null | undefined,
  tz: string,
): string {
  if (!utcIso) return "";
  const ref = new Date(utcIso);
  const year = formatInTimeZone(ref, tz, "yyyy");
  const currentYear = formatInTimeZone(new Date(), tz, "yyyy");
  const datePart =
    year === currentYear
      ? formatInTimeZone(ref, tz, "MMM d")
      : formatInTimeZone(ref, tz, "MMM d, yyyy");
  const time = formatInTimeZone(ref, tz, "HH:mm");
  const abbr = formatInTimeZone(ref, tz, "zzz");
  return `on ${datePart} at ${time} ${abbr}`;
}
